<script>
import { mapActions, mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import SandboxUserMessage from './sandbox/SandboxUserMessage.vue';
import SandboxBotMessage from './sandbox/SandboxBotMessage.vue';
import SandboxTypingIndicator from './sandbox/SandboxTypingIndicator.vue';
import SandboxInstructionsPanel from './sandbox/SandboxInstructionsPanel.vue';
import { getOnboardingKbSession } from '../../../../helper/onboardingKbSession';

const LOW_CREDIT_THRESHOLD = 5;

export default {
  name: 'OnboardingSandboxStep',
  components: {
    SandboxUserMessage,
    SandboxBotMessage,
    SandboxTypingIndicator,
    SandboxInstructionsPanel,
  },
  setup() {
    const { formatMessage } = useMessageFormatter();
    return { formatMessage };
  },
  data() {
    return {
      messageContent: '',
    };
  },
  computed: {
    ...mapGetters('sandbox', [
      'messages',
      'creditsUsed',
      'creditLimit',
      'creditsRemaining',
      'isBlocked',
      'isLoadingStatus',
      'isSending',
      'quotaExceeded',
      'suggestions',
    ]),
    ...mapGetters('onboardingKnowledge', {
      knowledgeDatasetId: 'datasetId',
    }),
    accountId() {
      return this.$route.params.accountId;
    },
    isLowQuota() {
      return this.creditsRemaining <= LOW_CREDIT_THRESHOLD;
    },
    canSend() {
      return (
        !this.isSending &&
        !this.isBlocked &&
        !this.quotaExceeded &&
        this.messageContent.trim().length > 0
      );
    },
  },
  watch: {
    // Covers navigating between two accounts' sandbox steps without a full
    // remount (Vue Router reuses the component instance when only the route
    // params change) — mounted() alone would miss this.
    accountId() {
      this.initialize();
    },
  },
  async mounted() {
    await this.initialize();
  },
  methods: {
    async initialize() {
      // onboardingKnowledge is a single SPA-lifetime module — reset it
      // before reading knowledgeDatasetId below, otherwise a dataset id
      // left over from a previously-onboarded account could get handed to
      // this account's sandbox session as its fallback dataset.
      this.resetIfAccountChanged();
      await this.fetchStatus();
      if (this.isBlocked) {
        // In-memory id first, then the one remembered across a refresh — both
        // are the dataset this wizard actually built, unlike ensureDatasetId's
        // own last resort of guessing "the account's latest dataset".
        await this.ensureDatasetId(
          this.knowledgeDatasetId ||
            getOnboardingKbSession(this.$route.params.accountId)?.datasetId
        );
      }
      if (!this.isBlocked) {
        await this.fetchMessages();
        this.fetchSuggestions();
        this.scrollToBottom();
      }
    },
    ...mapActions('sandbox', [
      'fetchStatus',
      'ensureDatasetId',
      'fetchMessages',
      'sendMessage',
      'fetchSuggestions',
      'rateMessage',
      'noteInstructionUpdated',
    ]),
    ...mapActions('onboardingKnowledge', ['resetIfAccountChanged']),
    handleInstructionsUpdated({ instructionChanged, modelChanged } = {}) {
      let noteKey =
        'ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.UPDATED_NOTE_INSTRUCTION';
      if (instructionChanged && modelChanged) {
        noteKey =
          'ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.UPDATED_NOTE_BOTH';
      } else if (modelChanged) {
        noteKey =
          'ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.UPDATED_NOTE_MODEL';
      }
      this.noteInstructionUpdated(this.$t(noteKey));
      this.scrollToBottom();
    },
    handleSend() {
      this.sendText(this.messageContent);
    },
    handleSuggestionClick(question) {
      this.sendText(question);
    },
    async sendText(text) {
      const messageText = (text || '').trim();
      if (
        !messageText ||
        this.isSending ||
        this.isBlocked ||
        this.quotaExceeded
      )
        return;

      this.messageContent = '';
      this.scrollToBottom();

      try {
        await this.sendMessage(messageText);
      } catch (error) {
        // sendMessage already surfaces a useAlert for the cases that need
        // one (503); other errors are silent here to avoid double-alerting.
      } finally {
        this.scrollToBottom();
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const list = this.$refs.messageList;
        if (!list) return;
        list.scrollTop = list.scrollHeight;
        // $nextTick only guarantees Vue's DOM patch is done, not that the
        // browser has finished laying out newly-inserted content (e.g. the
        // citations block's text metrics) — a longer suggestion-triggered
        // reply is more likely to still be settling. One more scroll after
        // a paint catches any height added by that late layout pass.
        requestAnimationFrame(() => {
          list.scrollTop = list.scrollHeight;
        });
      });
    },
    handleFeedback(messageId, rating) {
      this.rateMessage({ messageId, rating }).catch(() => {
        // rollback already handled in the store action
      });
    },
    handleContinue() {
      this.$router.push({
        name: 'onboarding_channel',
        params: { accountId: this.accountId },
      });
    },
    handleGoToStep1() {
      this.$router.push({
        name: 'onboarding_welcome',
        params: { accountId: this.accountId },
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4 p-6 md:p-10 w-full h-full">
    <h2 class="text-xl font-medium text-slate-900 dark:text-slate-100">
      {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.HEADING') }}
    </h2>

    <!-- Loading -->
    <div
      v-if="isLoadingStatus && isBlocked === null"
      class="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-500 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg"
    >
      {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.LOADING') }}
    </div>

    <!-- Blocked: no knowledge base created yet -->
    <div
      v-else-if="isBlocked"
      class="flex-1 flex flex-col items-center justify-center gap-3 text-slate-400 dark:text-slate-500 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg"
    >
      <p>{{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.BLOCKED.MESSAGE') }}</p>
      <woot-button @click="handleGoToStep1">
        {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.BLOCKED.CTA') }}
      </woot-button>
    </div>

    <!-- Chat UI -->
    <template v-else>
      <div class="flex flex-col md:flex-row gap-6 flex-1 min-h-0">
        <SandboxInstructionsPanel @updated="handleInstructionsUpdated" />

        <div class="flex flex-col gap-4 flex-1 min-h-0 min-w-0">
          <div
            ref="messageList"
            class="flex-1 min-h-0 overflow-auto border border-slate-100 dark:border-slate-800 rounded-lg p-4"
          >
            <template v-for="message in messages">
              <SandboxUserMessage
                v-if="message.role === 'user'"
                :key="message.id"
                :message="formatMessage(message.content)"
              />
              <div
                v-else-if="message.role === 'system'"
                :key="message.id"
                class="flex items-center justify-center my-3"
              >
                <span
                  class="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 px-3 py-1 rounded-full bg-slate-50 dark:bg-slate-800"
                >
                  <fluent-icon icon="wand" size="12" />
                  {{ message.content }}
                </span>
              </div>
              <SandboxBotMessage
                v-else
                :key="message.id"
                :content="message.content"
                :chunks="message.chunks"
                :rating="message.rating"
                @feedback="rating => handleFeedback(message.id, rating)"
              />
            </template>
            <SandboxTypingIndicator v-if="isSending" />
          </div>

          <div v-if="suggestions.length" class="flex flex-wrap gap-2">
            <button
              v-for="question in suggestions"
              :key="question"
              type="button"
              class="text-xs px-3 py-1.5 rounded-full border border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-woot-500 hover:text-woot-500"
              @click="handleSuggestionClick(question)"
            >
              {{ question }}
            </button>
          </div>

          <div class="flex flex-col gap-2">
            <textarea
              v-model="messageContent"
              data-testid="sandbox_composer_input"
              rows="3"
              class="resize-none block p-2.5 w-full text-sm text-slate-900 bg-slate-25 rounded-lg border border-slate-100 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:placeholder-slate-500 dark:text-white"
              :placeholder="
                $t('ONBOARDING_SHELL.STEPS.SANDBOX.COMPOSER_PLACEHOLDER')
              "
              :disabled="isSending || isBlocked || quotaExceeded"
              @keydown.meta.enter="handleSend"
              @keydown.ctrl.enter="handleSend"
            />
            <div class="flex items-center justify-between">
              <span
                class="text-xs"
                :class="
                  isLowQuota
                    ? 'text-yellow-600 dark:text-yellow-500'
                    : 'text-slate-400'
                "
              >
                {{
                  $t('ONBOARDING_SHELL.STEPS.SANDBOX.QUOTA_LABEL', {
                    used: creditsUsed,
                    limit: creditLimit,
                  })
                }}
              </span>
              <woot-button
                data-testid="sandbox_send_button"
                :disabled="!canSend"
                @click="handleSend"
              >
                {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.SEND_BUTTON') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="flex justify-end">
      <woot-button variant="clear" @click="handleContinue">
        {{ $t('ONBOARDING_SHELL.CONTINUE_BUTTON') }}
      </woot-button>
    </div>
  </div>
</template>
